import * as React from 'react'
import { usePaywallStore } from './usePaywallStore'
import { reasonLineFor, upgradeHintFor } from './paywallCopy'
import routes from '../../routes'

// What a free user sees the moment the server refuses something: why it was
// refused, and the way out. The purchase itself happens on /upgrade (owner,
// 2026-09-18: one screen, not a stepper), so this dialog stays small and never
// talks to Chargebee.
export default function UpgradeDialog() {
    const dialog = usePaywallStore(state => state.dialog)
    const closeUpgradeDialog = usePaywallStore(
        state => state.closeUpgradeDialog
    )
    const panelRef = React.useRef(null)

    React.useEffect(() => {
        if (!dialog.open) return undefined
        const onKeyDown = event => {
            if (event.key === 'Escape') closeUpgradeDialog()
        }
        document.addEventListener('keydown', onKeyDown)
        panelRef.current?.querySelector('a, button')?.focus()
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [dialog.open, closeUpgradeDialog])

    if (!dialog.open) return null

    const reason = dialog.reason
    const params = new URLSearchParams()
    if (reason && reason.code && reason.code !== 'MENU')
        params.set('reason', reason.code)
    if (typeof window !== 'undefined')
        params.set('return', window.location.pathname + window.location.search)
    const checkoutHref = `${routes.upgrade}?${params.toString()}`

    return (
        <div className="project-notes-scope">
            <div
                className="tw-fixed tw-inset-0 tw-z-[1400] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-p-4"
                onMouseDown={event => {
                    if (event.target === event.currentTarget)
                        closeUpgradeDialog()
                }}
            >
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="upgrade-dialog-title"
                    data-testid="upgrade-dialog"
                    className="tw-w-full tw-max-w-md tw-rounded-xl tw-border tw-border-solid tw-border-border tw-bg-background tw-p-6 tw-text-foreground tw-shadow-xl"
                >
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <p
                            id="upgrade-dialog-title"
                            className="tw-m-0 tw-text-lg tw-font-semibold"
                            data-testid="upgrade-reason"
                        >
                            {reasonLineFor(reason)}
                        </p>
                        <button
                            type="button"
                            onClick={closeUpgradeDialog}
                            aria-label="Close"
                            data-testid="upgrade-close"
                            className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-2 tw-py-1 tw-text-xl tw-leading-none tw-text-muted-foreground hover:tw-text-foreground"
                        >
                            ×
                        </button>
                    </div>

                    <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-muted-foreground">
                        {upgradeHintFor(reason)}
                    </p>

                    <div className="tw-mt-5 tw-flex tw-items-center tw-justify-end tw-gap-3">
                        <button
                            type="button"
                            onClick={closeUpgradeDialog}
                            data-testid="upgrade-dismiss"
                            className="tw-cursor-pointer tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-2 tw-py-2 tw-text-sm tw-text-muted-foreground hover:tw-text-foreground"
                        >
                            Not now
                        </button>
                        <a
                            href={checkoutHref}
                            data-testid="upgrade-continue"
                            className="tw-rounded-lg tw-bg-primary tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-primary-foreground tw-no-underline"
                        >
                            See plans and upgrade
                        </a>
                    </div>
                </div>
            </div>
        </div>
    )
}
